// =====================================================================
// src/lib/auth.js — o adaptador de sessão: entrar, sair, manter, chamar
//
// POR QUE NÃO O supabase-js: o app do Monteiro carrega o supabase-js pelo
// CDN e o index.html de lá documenta três contornos para travamentos do
// cliente em alguns Chromes (persistSession:false, refresh feito à mão por
// fetch, lock desligado). O que sobra de útil do supabase-js para ESTA
// tela são quatro chamadas HTTP. Fazê-las direto tira a dependência, tira
// os contornos, e deixa o adaptador testável em node com um fetch de
// mentira (scripts/teste_tela.js). Se um dia o app precisar de Realtime ou
// Storage, o supabase-js entra por trás DESTA interface — a tela não muda.
//
// A INTERFACE (tudo que a tela usa):
//   criarAuth({ url, chave, fetch, storage, agora })  → auth
//   auth.entrar(email, senha)   → sessão   (ou lança Error com mensagem em português)
//   auth.sair()                 → void     (apaga local; avisa o servidor se der)
//   auth.sessao()               → sessão | null   (renova se estiver vencendo)
//   auth.rpc(nome, args)        → o retorno da função (json)
//   auth.ler(tabela, consulta)  → linhas (json) de GET /rest/v1/<tabela>?<consulta>
//
// Erro do banco chega como Error com `.codigo` (SQLSTATE) e a MENSAGEM da
// função em português ("Devolver exige motivo…") — a tela mostra o texto
// como veio, sem traduzir de novo o que o banco já disse em português.
//
// `fetch`, `storage` e `agora` são injetáveis por um motivo só: teste.
// No navegador, main.js passa window.fetch, localStorage e Date.now.
// =====================================================================

const CHAVE_STORAGE = 'sgh.sessao';
const MARGEM_RENOVACAO_S = 60;   // renova se vence em menos de 1 min

// O que o GoTrue devolve num login/refresh, reduzido ao que se guarda.
function sessaoDe(json) {
  if (!json || !json.access_token || !json.refresh_token) return null;
  return {
    access_token:  json.access_token,
    refresh_token: json.refresh_token,
    // expires_at vem em segundos (epoch). Se não vier, calcula de expires_in.
    expires_at: json.expires_at || (Math.floor(Date.now() / 1000) + (json.expires_in || 3600)),
    usuario: json.user ? { id: json.user.id, email: json.user.email } : null,
  };
}

// Mensagens do GoTrue em português, sem vazar detalhe que ajude a enumerar
// contas: credencial errada é uma frase só, para e-mail inexistente OU senha
// errada.
function mensagemDeErro(status, json) {
  const codigo = (json && (json.error_code || json.error || json.code)) || '';
  const texto  = (json && (json.msg || json.message || json.error_description)) || '';
  if (status === 400 && /invalid_credentials|invalid_grant|Invalid login/i.test(codigo + ' ' + texto)) {
    return 'E-mail ou senha incorretos.';
  }
  if (status === 400 && /email_not_confirmed/i.test(codigo + ' ' + texto)) {
    return 'Este e-mail ainda não foi confirmado.';
  }
  if (status === 429) return 'Muitas tentativas. Aguarde um minuto e tente de novo.';
  if (status >= 500) return 'O serviço de login está indisponível. Tente de novo em instantes.';
  return texto ? `Não foi possível entrar (${texto}).` : `Não foi possível entrar (${status}).`;
}

export function criarAuth({ url, chave, fetch, storage, agora }) {
  if (!url || !chave) throw new Error('criarAuth: url e chave são obrigatórios');
  const base = String(url).replace(/\/$/, '');
  const now = agora || (() => Date.now());

  const ler = () => {
    try { const s = storage.getItem(CHAVE_STORAGE); return s ? JSON.parse(s) : null; }
    catch (_) { return null; }
  };
  const gravar = s => { try { if (s) storage.setItem(CHAVE_STORAGE, JSON.stringify(s)); else storage.removeItem(CHAVE_STORAGE); } catch (_) {} };

  async function chamarAuth(caminho, corpo, token) {
    const r = await fetch(base + '/auth/v1/' + caminho, {
      method: 'POST',
      headers: { apikey: chave, 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      body: corpo === undefined ? undefined : JSON.stringify(corpo),
    });
    const texto = await r.text();
    let json = null; try { json = texto ? JSON.parse(texto) : null; } catch (_) {}
    return { status: r.status, json };
  }

  async function entrar(email, senha) {
    const e = String(email || '').trim();
    if (!e || !senha) throw new Error('Informe e-mail e senha.');
    const r = await chamarAuth('token?grant_type=password', { email: e, password: senha });
    if (r.status !== 200) throw new Error(mensagemDeErro(r.status, r.json));
    const s = sessaoDe(r.json);
    if (!s) throw new Error('Resposta inesperada do login.');
    gravar(s);
    return s;
  }

  async function renovar(s) {
    const r = await chamarAuth('token?grant_type=refresh_token', { refresh_token: s.refresh_token });
    if (r.status !== 200) { gravar(null); return null; }   // refresh inválido = sessão morreu
    const nova = sessaoDe(r.json);
    gravar(nova);
    return nova;
  }

  // Devolve a sessão válida, renovando se estiver a menos de 1 min de
  // vencer. Nulo = não há sessão (ou ela morreu e foi apagada).
  async function sessao() {
    const s = ler();
    if (!s) return null;
    const faltam = s.expires_at - Math.floor(now() / 1000);
    if (faltam > MARGEM_RENOVACAO_S) return s;
    return renovar(s);
  }

  async function sair() {
    const s = ler();
    gravar(null);                                   // local primeiro: sair nunca falha para o usuário
    if (s) { try { await chamarAuth('logout', undefined, s.access_token); } catch (_) {} }
  }

  // Chama uma função de `public` via PostgREST com o token da sessão.
  // 401 aqui significa token recusado: apaga a sessão e devolve o erro,
  // para a tela voltar ao "entrar" em vez de girar para sempre.
  async function rpc(nome, args) {
    const s = await sessao();
    if (!s) throw new Error('sem sessão');
    const r = await fetch(base + '/rest/v1/rpc/' + nome, {
      method: 'POST',
      headers: { apikey: chave, Authorization: 'Bearer ' + s.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify(args || {}),
    });
    return resposta(r, `rpc ${nome}`);
  }

  // Lê linhas de uma tabela ou view de `public` (o RLS recorta). `consulta`
  // é a query string do PostgREST já montada: 'select=*&order=numero'.
  async function lerTabela(tabela, consulta) {
    const s = await sessao();
    if (!s) throw new Error('sem sessão');
    const r = await fetch(base + '/rest/v1/' + tabela + (consulta ? '?' + consulta : ''), {
      method: 'GET',
      headers: { apikey: chave, Authorization: 'Bearer ' + s.access_token, Accept: 'application/json' },
    });
    return resposta(r, `ler ${tabela}`);
  }

  async function resposta(r, oque) {
    const texto = await r.text();
    if (r.status === 401) { gravar(null); throw new Error('sessão recusada'); }
    if (r.status < 200 || r.status >= 300) {
      let json = null; try { json = JSON.parse(texto); } catch (_) {}
      // PostgREST devolve {code, message, details, hint}; `message` é a
      // mensagem do RAISE da função — a que o usuário deve ler.
      const e = new Error((json && json.message) ? json.message : `${oque}: ${r.status} ${texto.slice(0, 120)}`);
      e.codigo = json && json.code ? String(json.code) : String(r.status);
      e.status = r.status;
      throw e;
    }
    return texto ? JSON.parse(texto) : null;
  }

  // "Esqueci a senha": o GoTrue manda o e-mail com o link; a resposta é a
  // mesma exista o e-mail ou não (não se enumera conta por aqui).
  async function recuperar(email) {
    const e = String(email || '').trim();
    if (!e) throw new Error('Informe o e-mail.');
    const r = await chamarAuth('recover', { email: e });
    if (r.status === 429) throw new Error('Muitas tentativas. Aguarde um minuto e tente de novo.');
    if (r.status >= 500) throw new Error('O serviço de login está indisponível. Tente de novo em instantes.');
    if (r.status < 200 || r.status >= 300) throw new Error('Não foi possível pedir a redefinição.');
    return true;
  }

  // O link do e-mail (convite ou redefinição) chega com a sessão no hash
  // (#access_token=…&type=invite). Guarda a sessão e diz o tipo; a tela
  // então pede a senha nova antes de qualquer outra coisa.
  function aceitarLink(hash) {
    const l = sessaoDoHash(hash);
    if (!l) return null;
    gravar(l.sessao);
    return l.tipo;
  }

  // Define a senha da sessão atual (PUT /auth/v1/user). Serve para o convite,
  // para a redefinição e para "trocar minha senha" logado.
  async function definirSenha(senha) {
    if (!senha || String(senha).length < 8) throw new Error('A senha precisa ter pelo menos 8 caracteres.');
    const s = await sessao();
    if (!s) throw new Error('sem sessão');
    const r = await fetch(base + '/auth/v1/user', {
      method: 'PUT',
      headers: { apikey: chave, Authorization: 'Bearer ' + s.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: senha }),
    });
    const texto = await r.text();
    let json = null; try { json = JSON.parse(texto); } catch (_) {}
    if (r.status === 401) { gravar(null); throw new Error('O link expirou. Peça um novo.'); }
    if (r.status === 422 && /same_password|different from the old/i.test((json && (json.error_code + ' ' + json.msg)) || '')) throw new Error('A senha nova é igual à anterior.');
    if (r.status === 422 && /weak_password|Password should/i.test((json && (json.error_code + ' ' + json.msg)) || '')) throw new Error('Senha fraca: ' + (json.msg || 'escolha outra.'));
    if (r.status < 200 || r.status >= 300) throw new Error('Não foi possível definir a senha' + (json && json.msg ? ` (${json.msg})` : '.'));
    return true;
  }

  // Chama uma Edge Function (/functions/v1/<nome>) com o token da sessão.
  // O corpo de erro é {erro: texto em português} — a função foi escrita
  // para isso; qualquer outro formato vira mensagem genérica com o status.
  async function funcao(nome, corpo) {
    const s = await sessao();
    if (!s) throw new Error('sem sessão');
    const r = await fetch(base + '/functions/v1/' + nome, {
      method: 'POST',
      headers: { apikey: chave, Authorization: 'Bearer ' + s.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify(corpo || {}),
    });
    const texto = await r.text();
    let json = null; try { json = JSON.parse(texto); } catch (_) {}
    if (r.status === 401) { gravar(null); throw new Error('sessão recusada'); }
    if (r.status < 200 || r.status >= 300) {
      const e = new Error((json && json.erro) ? json.erro : `${nome}: ${r.status} ${texto.slice(0, 120)}`);
      e.status = r.status;
      throw e;
    }
    return json;
  }

  // ── STORAGE ────────────────────────────────────────────────────────
  // Sobe um arquivo para um bucket PRIVADO e devolve o CAMINHO dentro dele
  // (nunca uma URL): é o caminho que vai para o banco, e quem for ver pede
  // uma URL assinada de vida curta com `urlAssinada`. Guardar URL pública
  // numa coluna é o jeito de vazar foto de quarto para sempre.
  //
  // O nome do arquivo é sorteado e a extensão vem do tipo declarado pelo
  // navegador — nome de arquivo escolhido pelo usuário não entra em caminho
  // de bucket. `upsert` fica desligado: caminho novo a cada foto.
  async function enviarFoto(bucket, arquivo) {
    const s = await sessao();
    if (!s) throw new Error('sem sessão');
    if (!arquivo) throw new Error('Nenhum arquivo escolhido.');
    if (arquivo.size > 10 * 1024 * 1024) throw new Error('A foto tem mais de 10 MB. Tire outra com menos resolução.');
    const tipo = String(arquivo.type || '');
    if (!/^image\//.test(tipo)) throw new Error('Envie uma imagem.');
    const ext = ({ 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/heic': 'heic' })[tipo] || 'jpg';
    const hoje = new Date(now()).toISOString().slice(0, 10);
    const sorteio = (globalThis.crypto && globalThis.crypto.randomUUID)
      ? globalThis.crypto.randomUUID() : String(now()) + '-' + Math.random().toString(36).slice(2);
    const caminho = `${hoje}/${sorteio}.${ext}`;
    const r = await fetch(base + '/storage/v1/object/' + bucket + '/' + caminho, {
      method: 'POST',
      headers: { apikey: chave, Authorization: 'Bearer ' + s.access_token, 'Content-Type': tipo, 'x-upsert': 'false' },
      body: arquivo,
    });
    if (r.status === 401) { gravar(null); throw new Error('sessão recusada'); }
    if (r.status < 200 || r.status >= 300) {
      const t = await r.text();
      let json = null; try { json = JSON.parse(t); } catch (_) {}
      throw new Error((json && (json.message || json.error)) ? (json.message || json.error) : `não consegui enviar a foto (${r.status})`);
    }
    return caminho;
  }

  // URL assinada para VER um arquivo de bucket privado. Vida curta de
  // propósito: o endereço pode acabar copiado para um WhatsApp.
  async function urlAssinada(bucket, caminho, segundos = 300) {
    const s = await sessao();
    if (!s) throw new Error('sem sessão');
    const r = await fetch(base + '/storage/v1/object/sign/' + bucket + '/' + caminho, {
      method: 'POST',
      headers: { apikey: chave, Authorization: 'Bearer ' + s.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ expiresIn: segundos }),
    });
    const json = await resposta(r, `assinar ${bucket}`);
    return json && json.signedURL ? base + '/storage/v1' + json.signedURL : null;
  }

  return { entrar, sair, sessao, rpc, ler: lerTabela, recuperar, aceitarLink, definirSenha, funcao,
           enviarFoto, urlAssinada, _chaveStorage: CHAVE_STORAGE };
}

// Puro: o hash do endereço → { tipo, sessao } ou null. O GoTrue manda
// `#access_token=…&refresh_token=…&expires_in=3600&type=invite|recovery|…`.
// Sem access_token e refresh_token não é link de sessão (pode ser só #reservas).
export function sessaoDoHash(hash) {
  const h = String(hash || '').replace(/^#/, '');
  if (!h.includes('access_token=')) return null;
  const p = new URLSearchParams(h);
  const s = sessaoDe({ access_token: p.get('access_token'), refresh_token: p.get('refresh_token'),
    expires_in: Number(p.get('expires_in')) || 3600, expires_at: Number(p.get('expires_at')) || undefined });
  if (!s) return null;
  return { tipo: p.get('type') || 'desconhecido', sessao: s };
}
